import { Injectable } from '@angular/core';
import { environment } from '../../../environments/environment';
import { RuntimeConfigService } from './runtime-config.service';

export interface ApiConfig {
  baseUrl: string;
  timeout: number;
}

export interface SSOConfig {
  issuer: string;
  clientId: string;
  redirectUri: string;
}

export interface FeatureConfig {
  enableDebugLogging: boolean;
  enableAnalytics: boolean;
}

export interface AppConfig {
  production: boolean;
  name: string;
  api: ApiConfig;
  sso: SSOConfig;
  features: FeatureConfig;
}

@Injectable({
  providedIn: 'root'
})
export class ConfigService {
  private config: AppConfig = environment;
  private runtimeConfig: any = null;

  constructor(private runtimeConfigService: RuntimeConfigService) {
    if (this.config.features.enableDebugLogging) {
      console.log('App Configuration:', this.config);
    }
    
    // Load runtime configuration
    this.loadRuntimeConfig();
  }

  async loadRuntimeConfig() {
    try {
      this.runtimeConfig = await this.runtimeConfigService.loadConfig();
      if (this.runtimeConfig) {
        console.log('Runtime configuration loaded:', this.runtimeConfig);
      }
    } catch (error) {
      console.warn('Failed to load runtime configuration, using build-time config');
    }
  }

  getConfig(): AppConfig {
    return this.runtimeConfig || this.config;
  }

  getApiConfig(): ApiConfig {
    return this.runtimeConfig?.api || this.config.api;
  }

  getSSOConfig(): SSOConfig {
    return this.runtimeConfig?.sso || this.config.sso;
  }

  getFeatureConfig(): FeatureConfig {
    return this.runtimeConfig?.features || this.config.features;
  }

  isProduction(): boolean {
    return this.runtimeConfig?.production ?? this.config.production;
  }

  isDevelopment(): boolean {
    return !(this.runtimeConfig?.production ?? this.config.production);
  }

  getApiBaseUrl(): string {
    return this.runtimeConfig?.api?.baseUrl || this.config.api.baseUrl;
  }

  getSSOIssuer(): string {
    return this.runtimeConfig?.sso?.issuer || this.config.sso.issuer;
  }

  getSSOClientId(): string {
    return this.runtimeConfig?.sso?.clientId || this.config.sso.clientId;
  }

  getSSORedirectUri(): string {
    return this.runtimeConfig?.sso?.redirectUri || this.config.sso.redirectUri;
  }

  isDebugLoggingEnabled(): boolean {
    return this.runtimeConfig?.features?.enableDebugLogging ?? this.config.features.enableDebugLogging;
  }

  isAnalyticsEnabled(): boolean {
    return this.runtimeConfig?.features?.enableAnalytics ?? this.config.features.enableAnalytics;
  }
}
